import { useEffect, useMemo, useRef, useState } from 'react';
import { init, use, type ECharts, type ComposeOption } from 'echarts/core';
import { LineChart, type LineSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  MarkAreaComponent,
  type GridComponentOption,
  type LegendComponentOption,
  type TooltipComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { YAXisComponentOption } from 'echarts';
import { getMetrics, type MetricBucket, type MetricPoint } from '../api.js';
import { metricMeta, slotColor } from '../metrics.js';
import { buildChartQueries, type ChartSeriesSpec } from '../lib/chartQuery.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { formatMetric, displayUnit, type UnitPref } from '../lib/units.js';
import { spanDays as spanDaysBetween } from '../lib/range.js';
import { bandsToMarkArea, type ChartBand } from '../lib/concerns.js';
import {
  shouldFallBackToDay,
  seriesRenderMode,
  dataExtent,
  isTrimmedStart,
} from '../lib/density.js';

// Register only the pieces this chart uses — keeps ECharts out of the bundle
// wholesale (line series on a cartesian grid, with legend + axis tooltip, plus
// markArea for concern overlays).
use([LineChart, GridComponent, LegendComponent, TooltipComponent, MarkAreaComponent, CanvasRenderer]);

type ChartOption = ComposeOption<
  LineSeriesOption | GridComponentOption | LegendComponentOption | TooltipComponentOption
>;

export interface MetricChartProps {
  series: ChartSeriesSpec[];
  from: string;
  to: string;
  bucket: MetricBucket;
  /** Display-unit preference for values + axis/legend labels (imperial default). */
  unitPref?: UnitPref;
  /** Concern episodes to shade as translucent markArea bands. `to: null` = ongoing. */
  bands?: ChartBand[];
}

interface FetchedSeries {
  spec: ChartSeriesSpec;
  points: MetricPoint[];
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: FetchedSeries[]; spanDays: number; fromMs: number };

/**
 * Whether a fresh ECharts instance is needed for `el`. True when there is no
 * instance yet, when the instance was disposed, or when it is bound to a
 * different (e.g. remounted/detached) DOM node — calling setOption on a chart
 * whose node left the document renders nothing. Exported for tests.
 */
export function needsInit(
  chart: { getDom(): HTMLElement; isDisposed(): boolean } | null,
  el: HTMLElement,
): boolean {
  return chart === null || chart.isDisposed() || chart.getDom() !== el;
}

/** Chart chrome tokens per mode (data-viz "Chart chrome & ink"). */
function chrome(mode: 'light' | 'dark') {
  return mode === 'dark'
    ? { text: '#ffffff', muted: 'rgba(235,235,245,0.6)', grid: 'rgba(84,84,88,0.45)', axis: 'rgba(84,84,88,0.65)', surface: '#2c2c2e' }
    : { text: '#000000', muted: 'rgba(60,60,67,0.6)', grid: 'rgba(60,60,67,0.12)', axis: 'rgba(60,60,67,0.29)', surface: '#ffffff' };
}

export default function MetricChart({
  series,
  from,
  to,
  bucket,
  unitPref = 'imperial',
  bands,
}: MetricChartProps): JSX.Element {
  const mode = useThemeMode();
  const elRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  // Stable dependency for the render effect so band overlays reapply on change
  // without a churn of setOption on unrelated parent re-renders.
  const bandsKey = useMemo(() => JSON.stringify(bands ?? []), [bands]);

  // Stable key so identical props don't refetch on every render.
  const key = useMemo(
    () => JSON.stringify({ series, from, to, bucket }),
    [series, from, to, bucket],
  );

  useEffect(() => {
    let alive = true;
    const span = spanDaysBetween(from, to);
    const fromMs = new Date(from).getTime();
    if (series.length === 0) {
      setState({ status: 'ready', data: [], spanDays: span, fromMs });
      return;
    }
    setState({ status: 'loading' });
    const fetchAt = (b: MetricBucket): Promise<FetchedSeries[]> =>
      Promise.all(buildChartQueries({ series, from, to, bucket: b }).map((q) => getMetrics(q))).then(
        (responses) => responses.map((r, i) => ({ spec: series[i]!, points: r.points })),
      );
    fetchAt(bucket)
      .then(async (data) => {
        // Density-aware fallback: a finer-than-day bucket that came back sparse
        // gains nothing from its granularity, so re-fetch it at the day bucket.
        if (shouldFallBackToDay(bucket, data.map((s) => s.points.length), span)) {
          return fetchAt('day');
        }
        return data;
      })
      .then((data) => {
        if (!alive) return;
        setState({ status: 'ready', data, spanDays: span, fromMs });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to load metrics.',
        });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const showChart =
    state.status === 'ready' &&
    state.data.length > 0 &&
    state.data.some((s) => s.points.length > 0);

  // Apply the ECharts option whenever data or theme changes. The canvas div is
  // ALWAYS mounted (states render as overlays, never replacement returns), and
  // needsInit re-creates the instance if the node was ever swapped out — so a
  // "no metrics selected → reselect" round-trip can't strand a chart bound to a
  // detached DOM node.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    if (!showChart || state.status !== 'ready') {
      chartRef.current?.clear(); // don't leave stale marks behind an overlay
      return;
    }

    if (needsInit(chartRef.current, el)) {
      chartRef.current?.dispose();
      chartRef.current = init(el);
    }
    chartRef.current!.setOption(
      buildOption(state.data, mode, unitPref, state.spanDays, bands ?? []),
      true,
    );

    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, mode, showChart, unitPref, bandsKey]);

  useEffect(() => {
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  let overlay: JSX.Element | null = null;
  if (state.status === 'loading') {
    overlay = <div className="skeleton chart-canvas" />;
  } else if (state.status === 'error') {
    overlay = (
      <div className="state state--error">
        <div className="state__title">Couldn't load these metrics</div>
        <div className="state__hint">{state.message}</div>
      </div>
    );
  } else if (state.data.length === 0) {
    overlay = (
      <div className="state">
        <div className="state__title">Pick a metric to chart</div>
        <div className="state__hint">Choose one or more measures to overlay them here.</div>
      </div>
    );
  } else if (!showChart) {
    overlay = (
      <div className="state">
        <div className="state__title">No readings in this range</div>
        <div className="state__hint">
          Nothing recorded for the selected metrics yet. Widen the range or log data on Health.
        </div>
      </div>
    );
  }

  // When the data extent starts well inside the requested range (a sparse
  // series that only has recent points), the x-axis is clamped to the data and
  // we say so, so the chart doesn't imply a gap of "missing" earlier data.
  let note: string | null = null;
  if (state.status === 'ready' && showChart) {
    const ext = dataExtent(state.data.map((s) => s.points.map((p) => p.t)));
    if (ext && isTrimmedStart(ext.minMs, state.fromMs)) {
      note = `Showing data from ${new Date(ext.minMs).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`;
    }
  }

  return (
    <div className="chart-shell">
      <div
        ref={elRef}
        className="chart-canvas"
        style={{ visibility: showChart ? 'visible' : 'hidden' }}
        role="img"
        aria-label="Metric trend chart"
      />
      {overlay && <div className="chart-overlay">{overlay}</div>}
      {note && <div className="chart-note">{note}</div>}
    </div>
  );
}

/** Warn-tinted markArea fill/label per mode, kept subtle so bands never shout. */
function bandChrome(mode: 'light' | 'dark') {
  return mode === 'dark'
    ? { fill: 'rgba(224, 166, 58, 0.10)', border: 'rgba(224, 166, 58, 0.28)', label: '#e0a63a' }
    : { fill: 'rgba(198, 126, 21, 0.10)', border: 'rgba(198, 126, 21, 0.30)', label: '#c67e15' };
}

/** Compose the ECharts option: one y-axis per distinct unit, color per entity. */
function buildOption(
  data: FetchedSeries[],
  mode: 'light' | 'dark',
  pref: UnitPref,
  spanDays: number,
  bands: ChartBand[],
): ChartOption {
  const c = chrome(mode);

  // Distinct DISPLAY units, in first-seen order → axis index. Unitless metrics
  // (e.g. steps) share one internal key but show no axis caption.
  const UNITLESS = 'count';
  const units: string[] = [];
  for (const s of data) {
    const u = displayUnit(s.spec.type, pref) || UNITLESS;
    if (!units.includes(u)) units.push(u);
  }

  const yAxes: YAXisComponentOption[] = units.map((unit, i) => ({
    type: 'value',
    name: unit === UNITLESS ? '' : unit,
    position: i === 0 ? 'left' : 'right',
    offset: i > 1 ? (i - 1) * 52 : 0,
    nameTextStyle: { color: c.muted, fontSize: 13, align: i === 0 ? 'left' : 'right' },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: c.muted, fontSize: 13 },
    splitLine: { show: i === 0, lineStyle: { color: c.grid, type: [3, 3] } },
  }));

  const colors = data.map((s) => slotColor(metricMeta(s.spec.type).slot, mode));

  const seriesOpt: LineSeriesOption[] = data.map((s, i) => {
    const meta = metricMeta(s.spec.type);
    const unit = displayUnit(s.spec.type, pref) || UNITLESS;
    const unitLabel = displayUnit(s.spec.type, pref);
    const color = colors[i]!;
    // Sparse or short series get visible point symbols (and bridge gaps) so a
    // handful of readings don't render as an all-but-invisible line.
    const rm = seriesRenderMode(s.points.length, spanDays);
    return {
      name: `${meta.label}${unitLabel ? ` (${unitLabel})` : ''}`,
      type: 'line',
      yAxisIndex: units.indexOf(unit),
      showSymbol: rm.showSymbols,
      symbol: 'circle',
      symbolSize: 7,
      connectNulls: rm.connectNulls,
      smooth: 0.2,
      lineStyle: { width: 2.5, color, cap: 'round' },
      // A single series gets an Apple Health-style soft fill under its line.
      ...(data.length === 1
        ? {
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: `${color}33` },
                  { offset: 1, color: `${color}00` },
                ],
              },
            },
          }
        : {}),
      itemStyle: { color },
      emphasis: { focus: 'series' },
      data: s.points.map(
        (p) => [p.t, formatMetric(s.spec.type, p.v, pref).value] as [string, number],
      ),
    };
  });

  // Clamp the x-axis to where the data actually is, not the requested range, so
  // a sparse series isn't marooned in one corner of an empty span.
  const extent = dataExtent(data.map((s) => s.points.map((p) => p.t)));

  // Concern bands ride on the first series' markArea: a translucent warn-tinted
  // fill spanning each episode, its title revealed only on hover (no permanent
  // ink). Open episodes (to: null) run to the chart's right edge.
  if (bands.length > 0 && seriesOpt.length > 0) {
    const bc = bandChrome(mode);
    const chartEndIso = new Date(extent?.maxMs ?? Date.now()).toISOString();
    seriesOpt[0]!.markArea = {
      silent: false,
      itemStyle: { color: bc.fill, borderColor: bc.border, borderWidth: 1 },
      label: { show: false },
      emphasis: { label: { show: true, color: bc.label, position: 'insideTop', fontSize: 11 } },
      data: bandsToMarkArea(bands, chartEndIso),
    };
  }

  return {
    color: colors,
    grid: { left: 4, right: units.length > 1 ? 4 : 8, top: 40, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: c.surface,
      borderColor: 'transparent',
      borderWidth: 0,
      extraCssText: 'border-radius:12px;box-shadow:0 6px 24px rgba(0,0,0,.16);',
      textStyle: { color: c.text, fontSize: 13 },
      axisPointer: { type: 'line', lineStyle: { color: c.muted, width: 1 } },
    },
    legend: {
      top: 2,
      left: 0,
      textStyle: { color: c.text, fontSize: 13 },
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 14,
    },
    xAxis: {
      type: 'time',
      min: extent?.minMs,
      max: extent?.maxMs,
      axisLine: { lineStyle: { color: c.axis } },
      axisTick: { show: false },
      axisLabel: { color: c.muted, fontSize: 13, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: yAxes,
    series: seriesOpt,
  };
}
